import {
  BaseError,
  ConflictError,
  ForbiddenError,
  NotFoundError,
  PaymentRequiredError,
  ServiceUnavailableError,
  ThrottledError,
} from "@core/foundation/error";
import { hasValue } from "@core/foundation/types";

import type { HttpMethod } from "./http-method.ts";
import { StatusCodes } from "./status-codes.ts";

export class HttpStatusError extends BaseError {
  public readonly method: HttpMethod;

  public override readonly name = "HttpStatusError";

  public readonly statusCode: number;

  public readonly url: string;

  private constructor({
    method,
    statusCode,
    url,
    userPresentableMessage,
  }: {
    readonly method: HttpMethod;
    readonly statusCode: number;
    readonly url: string;
    readonly userPresentableMessage?: string;
  }) {
    const message = `Request failed with status code ${statusCode}`;

    super(
      hasValue(userPresentableMessage)
        ? { message, userPresentableMessage }
        : message,
    );

    this.method = method;
    this.statusCode = statusCode;
    this.url = url;
  }

  public static fromStatus({
    method,
    statusCode,
    url,
    userPresentableMessage,
  }: {
    readonly method: HttpMethod;
    readonly statusCode: number;
    readonly url: string;
    readonly userPresentableMessage?: string;
  }): BaseError | HttpStatusError {
    const message = `${method} ${url} failed with status code ${statusCode}`;

    const errorMessage = hasValue(userPresentableMessage)
      ? { message, userPresentableMessage }
      : message;

    switch (statusCode) {
      case StatusCodes.CONFLICT:
        return new ConflictError({
          message,
          userPresentableMessage:
            userPresentableMessage ??
            "The request conflicts with the current state.",
        });
      case StatusCodes.FORBIDDEN:
        return new ForbiddenError(errorMessage);
      case StatusCodes.NOT_FOUND:
        return new NotFoundError({
          message,
          userPresentableMessage:
            userPresentableMessage ?? "The requested item was not found.",
        });
      case StatusCodes.PAYMENT_REQUIRED:
        return new PaymentRequiredError({
          message,
          userPresentableMessage:
            userPresentableMessage ?? "The balance does not cover the request.",
        });
      case StatusCodes.SERVICE_UNAVAILABLE:
        return new ServiceUnavailableError({
          message,
          userPresentableMessage:
            userPresentableMessage ??
            "The service is unavailable. Please try again later.",
        });
      case StatusCodes.TOO_MANY_REQUESTS:
        return new ThrottledError(errorMessage);
      default:
        return new HttpStatusError({
          method,
          statusCode,
          url,
          userPresentableMessage,
        });
    }
  }

  public static getStatusCode(error: unknown): number | undefined {
    if (error instanceof HttpStatusError) {
      return error.statusCode;
    }

    if (error instanceof ForbiddenError) {
      return StatusCodes.FORBIDDEN;
    }

    if (error instanceof NotFoundError) {
      return StatusCodes.NOT_FOUND;
    }

    if (error instanceof PaymentRequiredError) {
      return StatusCodes.PAYMENT_REQUIRED;
    }

    if (error instanceof ConflictError) {
      return StatusCodes.CONFLICT;
    }

    if (error instanceof ThrottledError) {
      return StatusCodes.TOO_MANY_REQUESTS;
    }

    if (error instanceof ServiceUnavailableError) {
      return StatusCodes.SERVICE_UNAVAILABLE;
    }

    return undefined;
  }
}
